/**
 * Emit hosts for the `ask-ai` analytics group — 11 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  NoteInsertAction,
  NoteInsertEditorVariant,
} from '@/models/analytics/features/notes';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';

//AskAI
// The question is clinician-authored free text and routinely carries PHI,
// so only its length leaves the device.
export function askAi({
  questionAskedLength,
  sessionId,
}: {
  questionAskedLength: number;
  sessionId: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.AskAi,
    props: {
      action: SegmentActionName.AskAi,
      questionAskedLength,
      sessionId,
    },
  });
}

export function askAiAddAsNewOutput(mode?: string) {
  trackLegacyEvent({
    name: SegmentObjectName.AskAi,
    props: {
      action: SegmentActionName.AskAiAddAsNewOutput,
      mode,
    },
  });
}

export function askAiAddToNote(mode?: string) {
  trackLegacyEvent({
    name: SegmentObjectName.AskAi,
    props: {
      action: SegmentActionName.AskAiAddToNote,
      mode,
    },
  });
}

export function askAiCopy() {
  trackLegacyEvent({
    name: SegmentObjectName.AskAi,
    props: {
      action: SegmentActionName.AskAiCopy,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.NoteCopied,
    props: {
      copy_action: 'ask_ai_copy',
    },
  });
}

export function askAiRegenerate() {
  trackLegacyEvent({
    name: SegmentObjectName.AskAi,
    props: {
      action: SegmentActionName.AskAiRegenerate,
    },
  });
}

export function askAiReplaceNote(mode?: string) {
  trackLegacyEvent({
    name: SegmentObjectName.AskAi,
    props: {
      action: SegmentActionName.AskAiReplaceNote,
      mode,
    },
  });
}

export function askHeidiCitationClicked(params: {
  citationUrl: string;
  citationTitle: string;
  sessionId: string;
  citationSource: 'response' | 'sources';
}) {
  trackLegacyEvent({
    name: SegmentObjectName.AskAi,
    props: {
      action: SegmentActionName.AskHeidiCitationClicked,
      citationUrl: params.citationUrl,
      citationTitle: params.citationTitle,
      sessionId: params.sessionId,
      citationSource: params.citationSource,
    },
  });
  // V2 — Amplitude migration (askHeidiCitationClicked -> evidence_clicked)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceClicked,
    props: {
      click_action: 'citation_clicked',
      session_id: params.sessionId,
      citation_url: params.citationUrl,
      citation_title: params.citationTitle,
      citation_source: params.citationSource,
    },
  });
}

export function askHeidiClosed(mode?: string) {
  trackLegacyEvent({
    name: SegmentObjectName.AskAi,
    props: {
      action: SegmentActionName.AskHeidiClosed,
      mode,
    },
  });

  // V2 — Amplitude migration (askHeidiClosed -> panel_closed)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PanelClosed,
    props: {
      panel_type: 'ask_heidi',
      trigger: 'manual',
      mode: mode,
    },
  });
}

export function askHeidiCsatModalOpened(params: {
  mode?: string;
  feedbackType: 'positive' | 'negative';
}) {
  trackLegacyEvent({
    name: SegmentObjectName.AskAi,
    props: {
      action: SegmentActionName.AskHeidiCsatModalOpened,
      mode: params.mode,
      feedbackType: params.feedbackType,
    },
  });
  // V2 — Amplitude migration (askHeidiCsatModalOpened -> survey_viewed)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SurveyViewed,
    props: {
      view_action: 'csat_modal_opened',
      mode: params.mode,
      feedback_type: params.feedbackType,
    },
  });
}

export function askHeidiOpened() {
  trackLegacyEvent({
    name: SegmentObjectName.AskAi,
    props: {
      action: SegmentActionName.AskHeidiOpened,
    },
  });
  // V2 — Amplitude migration (askHeidiOpened -> panel_opened)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PanelOpened,
    props: {
      panel_type: 'ask_heidi',
      trigger: 'manual',
    },
  });
}

// Snowplow-first companion to the legacy askAi insert events: records the
// format a chat/Ask-Heidi insert stored (raw markdown vs converted HTML), to
// correlate the markdown-native insert path with `note_editor_render_failed`
// during the Labs rollout.
export function noteInsertApplied(params: {
  insertAction: NoteInsertAction;
  editorVariant: NoteInsertEditorVariant;
  sessionId?: string | null;
  mode?: string;
  chatId?: string | null;
  messageId?: string | null;
}) {
  track({
    event_name: TrackingEventName.NoteInsertApplied,
    props: {
      // Callers coalesce a missing session to '' — keep empty ids out of
      // the warehouse (`||`, not `??`, since `??` wouldn't strip '').
      session_id: params.sessionId || undefined,
      insert_action: params.insertAction,
      editor_variant: params.editorVariant,
      mode: params.mode,
      // chat_id attributes the adoption to the rollout cohort; message_id
      // additionally pins it to the producing model for per-message swaps (SBS).
      chat_id: params.chatId || undefined,
      message_id: params.messageId || undefined,
    },
    options: { amplitude: false },
  });
}
